/**
 * Utility functions for parsing and querying adminSettings data.
 */

export const PROCESSING_TYPE = {
  DIRECT: 'DIRECT',
  INDIRECT: 'INDIRECT'
};

/**
 * Extracts a Set of process names matching the given processingType from adminSettings.
 *
 * @param {object|null} adminSettings - Parsed adminSettings object
 * @param {string} processingType - One of PROCESSING_TYPE values (e.g., PROCESSING_TYPE.INDIRECT)
 * @returns {Set<string>} Set of matching process names (empty set if adminSettings is null)
 */
export function getProcessesByProcessingType(adminSettings, processingType) {
  const processSet = new Set();
  if (!adminSettings?.laborProcesses) return processSet;
  adminSettings.laborProcesses.forEach((p) => {
    if (p.processingType === processingType) processSet.add(p.name);
  });
  return processSet;
}

/**
 * Checks if a process name is an indirect process.
 *
 * @param {string} processName - The process name to check
 * @param {Set<string>} indirectProcesses - Set from getProcessesByProcessingType()
 * @returns {boolean} false if indirectProcesses is null/undefined or processName not found
 */
export function isIndirectProcess(processName, indirectProcesses) {
  if (!indirectProcesses) return false;
  return indirectProcesses.has(processName);
}

/**
 * Returns the optimizer presets configured in adminSettings, falling back to the provided default
 * when adminSettings is missing the key or it isn't a non-empty array. Each preset is expected to
 * have the shape { value, label, description, enabled }. Returns [] when no presets are configured.
 *
 * @param {object|null} adminSettings - Parsed adminSettings object
 * @returns {Array}
 */
export function getOptimizerPresets(adminSettings) {
  const presets = adminSettings?.optimizerPresets;
  if (Array.isArray(presets) && presets.length > 0) return presets;
  return [];
}
